import React, { useMemo, useState } from 'react';
import { AlertTriangle, Bot, FileUp, Loader2, X } from 'lucide-react';
import { Button } from '../Button';
import { analyzeSigePlanDocument } from '../../services/geminiService';
import { uploadDokument } from '../../services/projektDokumenteService';
import { ORDNER_JE_MODUL } from '../../services/unterlagenAblage';
import { ANLAGE_ABGEBROCHEN_TEXT, sigePlanVorlagenFuerAnlage, vorbelegungNeuerPlan } from '../../services/sigePlanVorlagen';
import { holdIdleExit } from '../../services/idleHold';
import { HAUPTABSCHNITTE } from './types';
import type { NotrufKontakt } from './types';
import { AI_PROMPTS } from './hooks/ki-prompts';
import { kiAntwortAlsObjekt } from './uebernahme';
import { altplanAusKi, leseAltplan } from './altplanLesen';
import type { AltplanErgebnis, AltplanSchritt } from './altplanLesen';
import { ALTPLAN_MAX_SEITEN, leseAltplanSeiten } from './altplanPdf';
import { altplanBauzeit, altplanStatus, altplanVersionsName, aufstellerAusProfil, baueAltplanAnlage } from './altplanAnlage';
import type { AltplanProfil } from './altplanAnlage';
import { legeAltplanAn } from './altplanSpeichern';

/** Was der Dialog vom angemeldeten Nutzer liest. */
export interface AltplanNutzer extends AltplanProfil {
  id?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

interface Props {
  projectId: string;
  nutzer?: AltplanNutzer | null;
  /** Anzahl der vorhandenen Planversionen — für den Hinweis zur Einordnung. */
  vorhandeneVersionen: number;
  onClose: () => void;
  /** Version steht in der Datenbank; der Editor lädt sie und zeigt die Meldung. */
  onAngelegt: (planId: string, meldung: { ton: 'erfolg' | 'warnung'; text: string }) => void;
}

/** Größte Datei für die KI (Inline-Grenze des Proxys, wie im LV-Import). */
const KI_MAX_BYTES = 13 * 1024 * 1024;

const deutsch = (iso?: string | null): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

const STATUS_TEXT = { entwurf: 'Entwurf', geprueft: 'Geprüft', ueberarbeitet: 'Überarbeitet' } as const;

const fehlerText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const leseBase64 = (datei: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const teil = String(reader.result || '').split(',')[1];
      if (teil) resolve(teil);
      else reject(new Error('Die Datei konnte nicht gelesen werden (leerer Inhalt).'));
    };
    reader.onerror = () => reject(reader.error || new Error('Die Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(datei);
  });

const kontaktText = (k?: NotrufKontakt): string => [k?.name, k?.adresse, k?.telefon].filter(Boolean).join(' · ');

/**
 * „Altplan einlesen“: ein SiGe-Plan aus der Zeit vor der App wird als PDF
 * gelesen (altplanLesen.ts), zur Prüfung gezeigt und als neue Planversion
 * angelegt. Angelegt wird erst auf Knopfdruck; bis dahin schreibt der Dialog
 * nichts.
 */
export const AltplanImportDialog: React.FC<Props> = ({ projectId, nutzer, vorhandeneVersionen, onClose, onAngelegt }) => {
  const [datei, setDatei] = useState<File | null>(null);
  const [phase, setPhase] = useState<'auswahl' | 'liest' | 'vorschau' | 'speichert'>('auswahl');
  const [ergebnis, setErgebnis] = useState<AltplanErgebnis | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versionLabel, setVersionLabel] = useState('');
  const [originalAblegen, setOriginalAblegen] = useState(true);
  const [kiLaeuft, setKiLaeuft] = useState(false);

  const lese = async (f: File) => {
    setDatei(f);
    setErgebnis(null);
    setFehler(null);
    if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') {
      setFehler('Bitte den Altplan als PDF wählen.');
      return;
    }
    setPhase('liest');
    try {
      const { seiten, seitenGesamt } = await leseAltplanSeiten(await f.arrayBuffer());
      const e = leseAltplan(seiten);
      if (seitenGesamt > ALTPLAN_MAX_SEITEN) {
        e.hinweise.push(`Nur die ersten ${ALTPLAN_MAX_SEITEN} von ${seitenGesamt} Seiten gelesen.`);
        // Was hinter der Grenze liegt, ist ungelesen wie eine Scanseite.
        for (let nr = ALTPLAN_MAX_SEITEN + 1; nr <= seitenGesamt; nr++) e.ungeleseneSeiten.push(nr);
      }
      setErgebnis(e);
      setVersionLabel(altplanVersionsName(e.kopf));
      setPhase('vorschau');
    } catch (e) {
      console.error('Altplan konnte nicht gelesen werden', e);
      setFehler(`Das PDF konnte nicht gelesen werden: ${fehlerText(e)}`);
      setPhase('auswahl');
    }
  };

  /**
   * Rückfall für Scans, fremde Vorlagen und teilweise gelesene Pläne — die KI
   * schreibt den GANZEN Plan ab. Scheitert sie, bleibt ein schon gelesenes
   * Ergebnis aus der Textebene stehen.
   */
  const leseMitKi = async () => {
    if (!datei) return;
    if (datei.size > KI_MAX_BYTES) {
      setFehler('Die Datei ist für die KI zu groß (höchstens 13 MB).');
      return;
    }
    const vorher = ergebnis;
    setKiLaeuft(true);
    setPhase('liest');
    setFehler(null);
    const freigeben = holdIdleExit('Altplan wird von der KI gelesen');
    try {
      const antwort = await analyzeSigePlanDocument(await leseBase64(datei), 'application/pdf', AI_PROMPTS.ALTPLAN_ABSCHRIFT);
      const e = altplanAusKi(kiAntwortAlsObjekt(antwort));
      if (!e.erkannt && vorher?.erkannt) {
        setFehler(`Die KI konnte den Plan nicht lesen (${e.grund}) — es bleibt das Ergebnis aus der Textebene.`);
      } else {
        setErgebnis(e);
        setVersionLabel(altplanVersionsName(e.kopf));
      }
    } catch (e) {
      console.error('KI-Abschrift des Altplans fehlgeschlagen', e);
      setFehler(`Die KI konnte den Plan nicht lesen: ${fehlerText(e)}`);
    } finally {
      freigeben();
      setKiLaeuft(false);
      setPhase('vorschau');
    }
  };

  const anlegen = async () => {
    if (!ergebnis?.erkannt) return;
    setPhase('speichert');
    setFehler(null);
    const freigeben = holdIdleExit('Altplan wird angelegt');
    try {
      const vorlagen = await sigePlanVorlagenFuerAnlage();
      if (vorlagen.abgebrochen === true) throw new Error(ANLAGE_ABGEBROCHEN_TEXT);
      const anlage = baueAltplanAnlage(ergebnis, {
        projectId,
        versionLabel,
        plotBreiteMm: vorbelegungNeuerPlan(vorlagen.vorlagen).plot_breite_mm,
        aufsteller: aufstellerAusProfil(nutzer),
        neueId: () => crypto.randomUUID(),
      });
      const { planId, ausgelassen } = await legeAltplanAn(anlage);

      const warnungen: string[] = [];
      if (ausgelassen.length) {
        warnungen.push(`Angelegt ohne ${ausgelassen.join(', ')} — die Datenbank kennt diese Spalte(n) nicht.`);
      }
      // Das Original ist der Beleg, die Version eine Abschrift: es gehört
      // daneben. Ein Fehler hier nimmt die angelegte Version nicht zurück.
      if (originalAblegen && datei) {
        try {
          await uploadDokument({
            projectId,
            datei,
            dateiname: datei.name,
            entwurf: {
              kategorie: 'eigene_unterlagen',
              ordner: ORDNER_JE_MODUL.sigeplan,
              titel: `SiGe-Plan ${ergebnis.kopf.plan_nr || ''} (Altplan, Original)`.replace(/\s+/g, ' '),
              beschreibung: 'Original des eingelesenen Altplans aus der Zeit vor der App.',
              tags: ['Altplan'],
              module: ['sigeplan'],
              stand: deutsch(ergebnis.kopf.plan_datum) || undefined,
            },
            benutzerId: nutzer?.id,
            benutzerName: [nutzer?.first_name, nutzer?.last_name].filter(Boolean).join(' ') || nutzer?.email || undefined,
          });
        } catch (e) {
          warnungen.push(`Das Original-PDF wurde nicht in „Dokumente“ abgelegt: ${fehlerText(e)}`);
        }
      }
      onAngelegt(planId, {
        ton: warnungen.length ? 'warnung' : 'erfolg',
        text: [
          `Altplan eingelesen: ${anlage.arbeitsschritte.length} Arbeitsschritte als Version „${String(anlage.plan.version_label)}“.`
          + ' Bitte vor dem nächsten Druck mit dem Original abgleichen.',
          ...warnungen,
        ].join('\n'),
      });
    } catch (e) {
      console.error('Altplan konnte nicht angelegt werden', e);
      setFehler(`Anlegen fehlgeschlagen — es wurde nichts gespeichert. ${fehlerText(e)}`);
      setPhase('vorschau');
    } finally {
      freigeben();
    }
  };

  const gruppen = useMemo(() => {
    const map = new Map<string, AltplanSchritt[]>();
    (ergebnis?.schritte || []).forEach(s => {
      const key = s.hauptabschnitt || 'Ohne Abschnitt';
      map.set(key, [...(map.get(key) || []), s]);
    });
    return [...map.entries()];
  }, [ergebnis]);

  const eigeneAbschnitte = (ergebnis?.hauptabschnitte || []).filter(a => !(HAUPTABSCHNITTE as readonly string[]).includes(a));
  const bauzeit = ergebnis ? altplanBauzeit(ergebnis.schritte) : { von: '', bis: '' };
  const k = ergebnis?.kopf;
  const beschaeftigt = phase === 'liest' || phase === 'speichert';

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <FileUp className="w-5 h-5 text-blue-600" />
            Altplan einlesen
          </h2>
          <button onClick={onClose} disabled={phase === 'speichert'} className="p-2 hover:bg-slate-200 rounded-lg text-slate-500 disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>

        {fehler && (
          <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 flex justify-between items-start gap-2">
            <span className="whitespace-pre-wrap">{fehler}</span>
            <button onClick={() => setFehler(null)} className="text-red-400 hover:text-red-600 px-1 shrink-0">✕</button>
          </div>
        )}

        <div className="p-5 flex-1 overflow-y-auto space-y-4">
          {phase === 'auswahl' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Ein SiGe-Plan aus der Zeit vor der App (Excel-Vorlage, als PDF gespeichert) wird gelesen und
                als neue Version dieses Plans angelegt — mit Plankopf, Abschnitten, allen Arbeitsschritten,
                Zeiträumen, Notrufplan und Legende. Vor dem Anlegen wird angezeigt, was gelesen wurde.
              </p>
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center bg-slate-50">
                <FileUp className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                <p className="text-slate-700 font-medium mb-3">Altplan als PDF auswählen</p>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  id="altplan_upload"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void lese(f); }}
                />
                <label htmlFor="altplan_upload" className="bg-white border border-slate-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 cursor-pointer shadow-sm">
                  Datei auswählen
                </label>
                {datei && <div className="mt-3 text-sm text-slate-500">{datei.name}</div>}
              </div>
            </div>
          )}

          {phase === 'liest' && (
            <div className="py-16 flex flex-col items-center gap-3 text-slate-500 text-sm">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              {kiLaeuft ? 'Die KI schreibt den Plan ab — das dauert bis zu zwei Minuten …' : 'Plan wird gelesen …'}
            </div>
          )}

          {(phase === 'vorschau' || phase === 'speichert') && ergebnis && !ergebnis.erkannt && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Der Plan ließ sich nicht direkt lesen.</p>
                  <p className="mt-1">{ergebnis.grund}</p>
                  <p className="mt-2 text-amber-800">
                    Die KI kann den Plan abschreiben. Das Ergebnis ist eine Abschrift, keine Kopie —
                    jede Zeile muss danach mit dem Original abgeglichen werden.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-3">
                <Button variant="ghost" onClick={() => { setErgebnis(null); setPhase('auswahl'); }}>Andere Datei</Button>
                {ergebnis.quelle === 'pdf' && (
                  <Button variant="ai" onClick={leseMitKi} disabled={beschaeftigt}>
                    <Bot className="w-4 h-4 mr-2" /> Mit KI lesen
                  </Button>
                )}
              </div>
            </div>
          )}

          {(phase === 'vorschau' || phase === 'speichert') && ergebnis?.erkannt && k && (
            <div className="space-y-4">
              <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-700">{datei?.name}</span>
                <span className={`px-2 py-0.5 rounded-full border ${ergebnis.quelle === 'ki' ? 'bg-violet-50 border-violet-200 text-violet-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                  {ergebnis.quelle === 'ki' ? 'von der KI abgeschrieben' : 'aus der Textebene gelesen'}
                </span>
              </div>

              <section className="border border-slate-200 rounded-xl p-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Plankopf</h3>
                <dl className="grid grid-cols-[8.5rem_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-slate-500">Bauherr</dt><dd>{[k.bauherr_name, k.bauherr_adresse].filter(Boolean).join(', ') || '—'}</dd>
                  <dt className="text-slate-500">Baumaßnahme</dt><dd>{[k.baumassnahme_name, k.baumassnahme_adresse].filter(Boolean).join(', ') || '—'}</dd>
                  <dt className="text-slate-500">Plan-Nr. / Datum</dt><dd>{k.plan_nr || '—'} · {deutsch(k.plan_datum) || '—'}</dd>
                  <dt className="text-slate-500">Bauzeit</dt><dd>{bauzeit.von ? `${deutsch(bauzeit.von)} – ${deutsch(bauzeit.bis)}` : '—'}</dd>
                  <dt className="text-slate-500">SiGeKo</dt><dd>{[k.sigeko_name, k.sigeko_mobil].filter(Boolean).join(' · ') || '—'}</dd>
                  <dt className="text-slate-500">Stempel</dt>
                  <dd>
                    {[
                      k.bearbeitet_datum || k.bearbeitet_name ? `bearbeitet ${deutsch(k.bearbeitet_datum)} ${k.bearbeitet_name}`.trim() : '',
                      k.geprueft_datum || k.geprueft_name ? `geprüft ${deutsch(k.geprueft_datum)} ${k.geprueft_name}`.trim() : '',
                      k.ueberarbeitet_datum ? `überarbeitet ${deutsch(k.ueberarbeitet_datum)}` : '',
                    ].filter(Boolean).join(' · ') || '—'}
                    <span className="text-slate-400"> → Status „{STATUS_TEXT[altplanStatus(k)]}“</span>
                  </dd>
                </dl>
              </section>

              <section className="border border-slate-200 rounded-xl p-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {ergebnis.schritte.length} Arbeitsschritte in {gruppen.length} Abschnitten
                </h3>
                <div className="space-y-1">
                  {gruppen.map(([abschnitt, schritte]) => (
                    <details key={abschnitt} className="group">
                      <summary className="cursor-pointer text-sm text-slate-700 py-1 hover:text-slate-900">
                        {abschnitt} <span className="text-slate-400">({schritte.length})</span>
                      </summary>
                      <ul className="ml-4 mb-2 space-y-1.5">
                        {schritte.map((s, i) => (
                          <li key={i} className="text-xs text-slate-600">
                            <span className="font-medium text-slate-800">{s.bezeichnung}</span>
                            <span className="text-slate-400">
                              {' — '}{s.zeitraum_von ? `${deutsch(s.zeitraum_von)} – ${deutsch(s.zeitraum_bis) || '?'}` : 'ohne Zeitraum'}
                              {' · '}{s.schutzmassnahmen.length} Maßnahmen · {s.vorschriften.length} Vorschriften
                              {s.zustaendig_erstellung ? ` · ${s.zustaendig_erstellung}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ))}
                </div>
              </section>

              <section className="border border-slate-200 rounded-xl p-4 text-sm">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Randblöcke</h3>
                <ul className="space-y-1 text-slate-700">
                  <li><span className="text-slate-500">Krankenhaus:</span> {kontaktText(ergebnis.notruf.krankenhaus) || '—'}</li>
                  <li><span className="text-slate-500">Durchgangsarzt:</span> {kontaktText(ergebnis.notruf.durchgangsarzt) || '—'}</li>
                  <li><span className="text-slate-500">Augenarzt:</span> {kontaktText(ergebnis.notruf.augenarzt) || '—'}</li>
                  <li><span className="text-slate-500">Apotheke:</span> {kontaktText(ergebnis.notruf.apotheke) || '—'}</li>
                  <li><span className="text-slate-500">Ersthelfer:</span> {ergebnis.notruf.ersthelfer.length || 'keine eingetragen'}</li>
                  <li><span className="text-slate-500">Legende:</span> {ergebnis.legende.length} Einträge</li>
                </ul>
              </section>

              {ergebnis.quelle === 'pdf' && ergebnis.ungeleseneSeiten.length > 0 && (
                <section className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex flex-wrap items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900 flex-1 min-w-[16rem]">
                    <strong>Nicht alle Seiten gelesen:</strong>{' '}
                    {ergebnis.ungeleseneSeiten.length === 1 ? 'Seite' : 'Seiten'} {ergebnis.ungeleseneSeiten.join(', ')} enthielten
                    keine lesbare Plantabelle. Stehen dort Zeilen des Plans, fehlen sie — dann den ganzen Plan von der KI abschreiben lassen.
                  </p>
                  <Button variant="ai" onClick={leseMitKi} disabled={beschaeftigt} className="shrink-0">
                    <Bot className="w-4 h-4 mr-2" /> Mit KI lesen
                  </Button>
                </section>
              )}

              {ergebnis.hinweise.length > 0 && (
                <section className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-amber-800 mb-1.5">Beim Lesen aufgefallen</h3>
                  <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
                    {ergebnis.hinweise.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </section>
              )}

              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1">
                <p>Piktogramme und Logos sind im PDF Bilder und werden nicht gelesen — der Plan erhält die Standard-Gebots- und Warnzeichen.</p>
                <p>Das Gewerk bleibt leer; wer zuständig ist, steht wie im Original in Erstellung, Kontrolle und Instandhaltung.</p>
                {eigeneAbschnitte.length > 0 && (
                  <p>Eigene Abschnitte ({eigeneAbschnitte.map(a => `„${a}“`).join(', ')}) druckt die App hinter den Standardabschnitten.</p>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Name der Version</label>
                  <input
                    type="text"
                    value={versionLabel}
                    onChange={e => setVersionLabel(e.target.value)}
                    className="w-full p-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {vorhandeneVersionen > 0
                      ? (k.plan_datum
                        ? `Die Version wird nach ihrem Plandatum (${deutsch(k.plan_datum)}) in die Versionsliste einsortiert.`
                        : 'Ohne Plandatum erscheint sie als neueste Version.')
                      : 'Es ist die erste Version dieses Plans.'}
                  </p>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={originalAblegen} onChange={e => setOriginalAblegen(e.target.checked)} />
                  Original-PDF in „Dokumente“ ablegen (Ordner „{ORDNER_JE_MODUL.sigeplan}“)
                </label>
              </div>
            </div>
          )}
        </div>

        {(phase === 'vorschau' || phase === 'speichert') && ergebnis?.erkannt && (
          <div className="p-4 border-t border-slate-200 bg-slate-50 flex flex-wrap justify-end gap-3">
            <Button variant="ghost" onClick={() => { setErgebnis(null); setPhase('auswahl'); }} disabled={beschaeftigt}>
              Andere Datei
            </Button>
            <Button variant="primary" onClick={anlegen} disabled={beschaeftigt}>
              {phase === 'speichert' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Als Version anlegen ({ergebnis.schritte.length} Arbeitsschritte)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
